import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageSwitch } from "../../hooks/usePageSwitch";
import Index from "./Index";
import Header from "../../components/common/Header";
import Gallery from "./Gallery";
import Members from "./Members";
import "../../styles/global.css";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

const PAGE_INDEX = "index";
const PAGE_GALLERY = "gallery";
const PAGE_MEMBERS = "members";

function HomePage(props) {
  useDocumentTitle("天地源")
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const pageList = [PAGE_INDEX, PAGE_GALLERY, PAGE_MEMBERS]; //页面
  const [isSwitchLocked, setIsSwitchLocked] = useState(false); //界面切换锁
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const {
    currentPage,
    visibility,
    isTaExist,
    taX,
    taR,
    shouldHeadStyle2Active,
    switchPage,
    isAnimating,
    setPageVisibility,
  } = usePageSwitch({
    pageList,
    defaultPage: PAGE_INDEX,
    onPageChange: (page) => {
      navigate(`#${page}`, { replace: true });
    },
  });

  // 根据URL锚点初始化页面
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (pageList.includes(hash) && hash !== currentPage) {
      switchPage(hash);
      if (isSwitchLocked) {
        setIsSwitchLocked(false); //防止卡死
      }
    }
    if (!isFirstLoad) {
      for (let a of pageList) {
        setPageVisibility(a, hash === a);
      }
      if (hash === "") {
        setPageVisibility(PAGE_INDEX, true);
      }
      setIsFirstLoad(true);
    }
  }, [
    location.hash,
    currentPage,
    switchPage,
    pageList,
    isSwitchLocked,
    isFirstLoad,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const pageIndices = pageList.reduce(
      (acc, p, i) => ({ ...acc, [p]: i }),
      {},
    );

    const handleWheel = (e) => {
      if (!isSwitchLocked) {
        e.preventDefault();
      }

      if (isAnimating || isSwitchLocked) return;

      const scrollAmount = e.deltaY || e.detail || e.wheelDelta;
      const isScrollingDown = scrollAmount > 0;
      const currentIdx = pageIndices[currentPage];

      if (isScrollingDown && currentIdx < pageList.length - 1) {
        switchPage(pageList[currentIdx + 1]);
      } else if (!isScrollingDown && currentIdx > 0) {
        switchPage(pageList[currentIdx - 1]);
      }
    };

    // 触摸
    let touchStartY = 0;
    let touchStartX = 0;
    let touchEndY = 0;
    let touchEndX = 0;
    let isScrolling = false;
    let touchStartTime = 0;

    const handleTouchStart = (e) => {
      if (isAnimating || isSwitchLocked) return;

      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
      touchStartTime = Date.now();
      isScrolling = true;
    };

    const handleTouchMove = (e) => {
      if (!isScrolling || isAnimating || isSwitchLocked) return;

      // 更新结束位置
      touchEndY = e.touches[0].clientY;
      touchEndX = e.touches[0].clientX;
      if (!isSwitchLocked) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (!isScrolling || isAnimating || isSwitchLocked) {
        isScrolling = false;
        return;
      }

      const deltaY = touchStartY - touchEndY;
      const deltaX = touchStartX - touchEndX;

      const touchDuration = Date.now() - touchStartTime;

      // 判断是否为垂直滑动

      let isVerticalSwipe = !(
        touchEndY === 0 &&
        !(Math.abs(deltaX) === 0 || Math.abs(deltaY) / Math.abs(deltaX) > 2)
      );
      if(touchEndY===0){
        isVerticalSwipe=false;
      }
      if (Math.abs(deltaY) > 30 && touchDuration < 300 && isVerticalSwipe) {
        const isSwipingDown = deltaY > 0;
        const currentIdx = pageIndices[currentPage];
        
        if (isSwipingDown && currentIdx < pageList.length - 1) {
          switchPage(pageList[currentIdx + 1]);
        } else if (!isSwipingDown && currentIdx > 0) {
          switchPage(pageList[currentIdx - 1]);
        }
      }

      isScrolling = false;
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [currentPage, switchPage, isAnimating, pageList, isSwitchLocked]);

  const pageIndices = pageList.reduce((acc, p, i) => ({ ...acc, [p]: i }), {});

  return (
    <div className="home">
      <Header
        isLoading={props.isLoading}
        shouldHeadStyle2Active={shouldHeadStyle2Active}
        nowPage={pageIndices[currentPage] + 1}
      />
      <div
        className="header-line"
        style={
          !visibility[PAGE_MEMBERS]
            ? { opacity: 0, transform: " translateY(-50%)" }
            : { opacity: 1, transform: "translateY(0)" }
        }
      ></div>
      <div
        ref={containerRef}
        className="home-page"
        style={{ height: visibility[PAGE_MEMBERS] ? "120vh" : "100vh" }}
      >
        {!(
          props.isLoading ||
          !(visibility[PAGE_INDEX] || visibility[PAGE_GALLERY])
        ) && (
          <div id="index" className="home-index">
            <Index shouldShow={visibility[PAGE_INDEX]} />
          </div>
        )}

        {visibility[PAGE_GALLERY] && (
          <div id="gallery" className="home-gallery">
            <Gallery setSwitchLocked={setIsSwitchLocked} locker={setIsSwitchLocked} lockStatus={isSwitchLocked} />
          </div>
        )}

        {!(props.isLoading || !visibility[PAGE_MEMBERS]) && (
          <div id="members">
            <Members locker={setIsSwitchLocked} />
          </div>
        )}

        {isTaExist && (
          <div
            className="home-ta"
            style={{
              width: `${taX}vw`,
              right: `${taR}vw`,
            }}
          />
        )}
      </div>
    </div>
  );
}

export default HomePage;
